import { useMemo } from "react";

import Card from "#/components/shared/card";
import ErrorDisplay from "#/components/shared/ErrorDisplay";
import HarperLoadingSpinner from "#/components/shared/HarperLoadingSpinner";
import { SubmitButton } from "#/components/shared/buttons/submit-button";
import Typography from "#/components/shared/typography";
import { useInboundDocumentJobsNeedingReview } from "../hooks/useInboundDocumentJobsNeedingReview";
import InboundDocumentJobList from "./InboundDocumentJobList";

export function InboundDocumentJobsLayout() {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useInboundDocumentJobsNeedingReview();

  const jobs = useMemo(
    () =>
      [...(data ?? [])].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [data]
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <HarperLoadingSpinner label="Loading inbound document jobs" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="space-y-2">
        <Typography variant="h2" className="text-terracotta">
          Inbound document jobs
        </Typography>
        <Typography variant="body1" className="text-brown-700">
          Pull the latest inbound document jobs that require review. This calls
          {" "}
          <code className="rounded bg-cream-100 px-1.5 py-0.5 text-sm">GET /api/inbound-document-jobs/needs-review</code>{" "}
          to mint a capability and proxy the medical API with your admin auth.
        </Typography>
      </div>

      <Card variant="default" elevation={1} className="w-full">
        <Card.Header variant="default" className="flex flex-col gap-2">
          <Typography variant="h4" className="text-terracotta">
            Needs review queue
          </Typography>
          <Typography variant="body2" className="text-brown-600">
            Refresh to pull new jobs. Each entry shows the capability-backed
            download URL when available plus the raw metadata payload for audit
            trails.
          </Typography>
        </Card.Header>
        <Card.Body className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <SubmitButton
              size="sm"
              hideArrow
              onClick={() => refetch()}
              isLoading={isFetching}
            >
              {isFetching ? "Refreshing" : "Refresh list"}
            </SubmitButton>
            <Typography variant="body2" className="text-brown-600">
              {jobs.length} job{jobs.length === 1 ? "" : "s"} need review
            </Typography>
          </div>

          {isError ? (
            <ErrorDisplay
              description={
                error instanceof Error
                  ? error.message
                  : "Unable to load inbound document jobs."
              }
              actions={
                <SubmitButton
                  size="sm"
                  hideArrow
                  onClick={() => refetch()}
                  isLoading={isFetching}
                >
                  Retry
                </SubmitButton>
              }
            />
          ) : null}

          {!isError && jobs.length === 0 ? (
            <div className="rounded-xl border border-cream-200 bg-cream-50 p-6 text-brown-700">
              <Typography variant="subtitle1" className="text-terracotta-800">
                Nothing needs review right now
              </Typography>
              <Typography variant="body2" className="text-brown-600">
                When the medical API flags inbound documents for manual review,
                they will appear here with their metadata and capability links.
              </Typography>
            </div>
          ) : null}

          {jobs.length > 0 ? <InboundDocumentJobList jobs={jobs} /> : null}
        </Card.Body>
      </Card>
    </div>
  );
}

export default InboundDocumentJobsLayout;
